import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";

const AdminAuthContext = createContext(null);
const KEY = "citydooh_admin_token";

export const AdminAuthProvider = ({ children }) => {
  const [admin, setAdmin] = useState(null);
  const [ready, setReady] = useState(false);

  const hydrate = useCallback(async () => {
    const token = localStorage.getItem(KEY);
    if (!token) { setReady(true); return; }
    try {
      const r = await api.adminMe(token);
      setAdmin(r.data);
    } catch {
      localStorage.removeItem(KEY);
      setAdmin(null);
    } finally {
      setReady(true);
    }
  }, []);

  useEffect(() => { hydrate(); }, [hydrate]);

  const loginAdmin = useCallback(async (email, password) => {
    const r = await api.adminLogin({ email, password });
    localStorage.setItem(KEY, r.data.token);
    setAdmin(r.data.admin);
    return r.data.admin;
  }, []);

  // Two-step superadmin login
  const loginSuperAdminStep1 = useCallback(async (email, password) => {
    const r = await api.superadminLoginStep1({ email, password });
    return r.data; // {otp_required, dev_otp_hint}
  }, []);
  const loginSuperAdminStep2 = useCallback(async (email, password, otp) => {
    const r = await api.superadminVerifyOTP({ email, password, otp });
    localStorage.setItem(KEY, r.data.token);
    setAdmin(r.data.admin);
    return r.data.admin;
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem(KEY);
    setAdmin(null);
  }, []);

  // Memoise context value — prevents cascading re-renders of every
  // admin-panel consumer whenever an ancestor component re-renders.
  const value = useMemo(
    () => ({ admin, ready, loginAdmin, loginSuperAdminStep1, loginSuperAdminStep2, logout, refresh: hydrate }),
    [admin, ready, loginAdmin, loginSuperAdminStep1, loginSuperAdminStep2, logout, hydrate],
  );

  return (
    <AdminAuthContext.Provider value={value}>
      {children}
    </AdminAuthContext.Provider>
  );
};

export const useAdminAuth = () => {
  const ctx = useContext(AdminAuthContext);
  if (!ctx) throw new Error("useAdminAuth must be used within AdminAuthProvider");
  return ctx;
};
